import { Dispatch, SVGAttributes, SetStateAction, useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { IconNames } from './IconNames';
import NotFoundImage from './paths/image-not-found.json';

const COLOR_CONFIG = {
  neutral: 'fill-neutral-500',
  blue: 'fill-blue-500',
  green: 'fill-green-500',
  red: 'fill-red-500',
  yellow: 'fill-yellow-500',
  white: 'fill-white',
  black: 'fill-neutral-900',
  current: 'fill-current',
};

type IconColor = keyof typeof COLOR_CONFIG;

const SIZE_CONFIG = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

type IconSize = keyof typeof SIZE_CONFIG;

interface IconProps extends SVGAttributes<SVGElement> {
  readonly icon: IconNames;
  readonly size?: IconSize;
  readonly color?: IconColor;
  readonly timeToFallback?: number;
}

function setDefault(
  setPath: Dispatch<
    SetStateAction<
      | (SVGAttributes<SVGPathElement> & {
          alt: string;
        })
      | undefined
    >
  >,
) {
  setPath(NotFoundImage);
}

function Icon({ icon, size = 'lg', color = 'current', height, width, timeToFallback = 300, className, ...props }: IconProps) {
  const [path, setPath] = useState<
    SVGAttributes<SVGPathElement> & {
      alt: string;
    }
  >();
  const imageSize = SIZE_CONFIG[size];
  const iconColor = COLOR_CONFIG[color] ?? color;

  useEffect(() => {
    const timeout = setTimeout(setDefault.bind({}, setPath), timeToFallback);

    import(`./paths/${icon}.json`)
      .then((p) => {
        setPath(p);
      })
      .catch(() => {
        setPath(NotFoundImage);
      })
      .finally(() => {
        clearTimeout(timeout);
      });
  }, [icon, timeToFallback]);

  const { alt, ...config } = path || {};

  return (
    <svg
      role="img"
      aria-label={alt}
      aria-busy={!path || path === NotFoundImage}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      className={twMerge('inline-block align-middle', iconColor, className)}
      height={height ?? imageSize}
      width={width ?? imageSize}
      {...props}
    >
      {alt ? <title title={alt}>{alt}</title> : null}
      {path ? <path fillRule="evenodd" clipRule="evenodd" {...config} /> : null}
    </svg>
  );
}

export { Icon, setDefault as _setDefault, type IconNames };
